import { z } from 'zod';
import { StatusCategory, WorkItemType } from './work-items.js';
/**
 * Sections, custom columns and project stages (docs/adr/0093). A project's list groups by its sections; its board shows
 * its stages (the workspace's statuses with the project's own labels, order and hidden ones, plus stages it added) or
 * its sections; custom columns are typed fields with one value per task.
 */
const Color = z.enum(['slate', 'blue', 'violet', 'red', 'green', 'orange']);
/** The categories a project's own stage can have: to do, in progress, review or done. */
export const ProjectStageCategory = z.enum(['todo', 'active', 'review', 'done']);
export const ProjectSection = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    name: z.string(),
    position: z.number(),
});
/** One stage of a project as the project sees it. `id` is a status id (`work_statuses`). */
export const ProjectWorkStage = z.object({
    id: z.uuid(),
    key: z.string(),
    /** The project's name for it (its own label, or the workspace's when not renamed here). */
    label: z.string(),
    /** The workspace's name for a workspace status; null for a stage this project added. */
    workspaceLabel: z.string().nullable(),
    category: StatusCategory,
    color: Color,
    position: z.number().int(),
    hidden: z.boolean(),
    /** A stage this project added (it can be deleted and its category changed). */
    own: z.boolean(),
    types: z.array(WorkItemType).nullable(),
});
export const FieldType = z.enum(['text', 'number', 'dropdown', 'person', 'date']);
export const FieldOption = z.object({
    id: z.string().regex(/^[a-z0-9]{1,16}$/),
    label: z.string().trim().min(1).max(40),
    color: Color,
});
export const ProjectField = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    name: z.string(),
    type: FieldType,
    options: z.array(FieldOption),
});
/** The built-in list columns, in their default order. */
export const BUILTIN_COLUMNS = ['assignee', 'due', 'stage', 'priority', 'estimate', 'actual'];
export const BuiltinColumn = z.enum(BUILTIN_COLUMNS);
/** A list column: a built-in, or `field:<id>` for a custom one. */
export const ColumnKey = z.union([BuiltinColumn, z.string().regex(/^field:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)]);
export const fieldColumnKey = (fieldId) => `field:${fieldId}`;
export const LayoutColumn = z.object({ key: ColumnKey, hidden: z.boolean() });
export const BoardBy = z.enum(['stage', 'section']);
/** A custom column's value: text, an option id and a person id are strings; a date is YYYY-MM-DD; a number is a number. */
export const FieldValue = z.union([z.string(), z.number()]);
export const FieldValues = z.record(z.string(), FieldValue);
/** Everything the project page's list and board need about the project's shape, in one read. */
export const ProjectWorkSetup = z.object({
    projectId: z.uuid(),
    sections: z.array(ProjectSection),
    /** Every stage, hidden ones included, in the project's order. */
    stages: z.array(ProjectWorkStage),
    /** The project has changed its stages (labels, order, hidden or its own). */
    stagesCustomized: z.boolean(),
    fields: z.array(ProjectField),
    /** The project's shared column order: every built-in and every field, each once. */
    columns: z.array(LayoutColumn),
    /** The caller's own choices for this project. */
    boardBy: BoardBy,
    hiddenColumns: z.array(ColumnKey),
    /** The caller may change sections, stages, columns and values (owners, admins and the project's members). */
    canEdit: z.boolean(),
});
export const CreateSectionInput = z.object({
    name: z.string().trim().min(1).max(80),
    /** Where it goes; leave out to add it at the end. */
    position: z.number().optional(),
});
export const UpdateSectionInput = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    position: z.number().optional(),
});
/** Deleting a section: its tasks move to `moveTo` (a section of the same project), or to No section. */
export const DeleteSectionQuery = z.object({ moveTo: z.uuid().optional() });
export const CreateProjectStageInput = z.object({
    label: z.string().trim().min(1).max(32),
    category: ProjectStageCategory.default('active'),
    color: Color.optional(),
});
export const UpdateProjectStageInput = z.object({
    label: z.string().trim().min(1).max(32).optional(),
    hidden: z.boolean().optional(),
    /** A stage this project added only. */
    category: ProjectStageCategory.optional(),
    color: Color.optional(),
});
/** The project's stages in their new order (every stage id once; hidden ones included). */
export const ReorderProjectStagesInput = z.object({ ids: z.array(z.uuid()).min(1).max(60) });
/** Deleting a project's own stage: its tasks move to `moveTo`, or to the first visible stage with the same category. */
export const DeleteProjectStageQuery = z.object({ moveTo: z.uuid().optional() });
const NewOption = z.object({ id: FieldOption.shape.id.optional(), label: FieldOption.shape.label, color: Color.optional() });
export const CreateProjectFieldInput = z.object({
    name: z.string().trim().min(1).max(40),
    type: FieldType,
    /** Dropdown only. */
    options: z.array(NewOption).max(50).optional(),
});
export const UpdateProjectFieldInput = z.object({
    name: z.string().trim().min(1).max(40).optional(),
    /** Dropdown only: the full new list. Options left out are removed and the values that used them cleared. */
    options: z.array(NewOption).max(50).optional(),
});
export const UpdateProjectLayoutInput = z.object({ columns: z.array(LayoutColumn).max(80) });
export const UpdateProjectViewPrefsInput = z.object({
    boardBy: BoardBy.optional(),
    hiddenColumns: z.array(ColumnKey).max(80).optional(),
});
/** Set one custom column value of a task; null clears it. */
export const SetFieldValueInput = z.object({ value: FieldValue.nullable() });
export const FieldValuesResponse = z.object({ fields: FieldValues });
export const TEXT_FIELD_MAX = 2000;
/**
 * The value a field of this type accepts, or null when it is not one. Text is trimmed (an empty one is no value), a
 * number must be finite, a dropdown value one of the field's option ids, a person a user id, a date YYYY-MM-DD.
 * The server also checks that a person is a member of the workspace.
 */
export function parseFieldValue(field, raw) {
    if (raw === null || raw === undefined)
        return { ok: true, value: null };
    switch (field.type) {
        case 'text': {
            const r = z.string().max(TEXT_FIELD_MAX * 2).safeParse(raw);
            if (!r.success)
                return { ok: false };
            const v = r.data.trim();
            if (v.length > TEXT_FIELD_MAX)
                return { ok: false };
            return { ok: true, value: v || null };
        }
        case 'number': {
            const r = z.number().finite().min(-1e12).max(1e12).safeParse(raw);
            return r.success ? { ok: true, value: r.data } : { ok: false };
        }
        case 'dropdown': {
            const r = z.string().safeParse(raw);
            if (!r.success)
                return { ok: false };
            if (r.data === '')
                return { ok: true, value: null };
            return field.options.some((o) => o.id === r.data) ? { ok: true, value: r.data } : { ok: false };
        }
        case 'person': {
            const r = z.uuid().safeParse(raw);
            return r.success ? { ok: true, value: r.data } : { ok: false };
        }
        case 'date': {
            const r = z.iso.date().safeParse(raw);
            return r.success ? { ok: true, value: r.data } : { ok: false };
        }
    }
}
/**
 * The full column order for a project: the saved layout's columns that still exist, then any built-in or field it
 * doesn't name, in default order. Each column once.
 */
export function resolveColumns(saved, fieldIds) {
    const valid = new Set([...BUILTIN_COLUMNS, ...fieldIds.map(fieldColumnKey)]);
    const out = [];
    const seen = new Set();
    for (const c of saved) {
        if (!valid.has(c.key) || seen.has(c.key))
            continue;
        seen.add(c.key);
        out.push({ key: c.key, hidden: !!c.hidden });
    }
    for (const k of valid)
        if (!seen.has(k))
            out.push({ key: k, hidden: false });
    return out;
}
//# sourceMappingURL=work-layout.js.map